/* Copyright (c) 2024-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Injectable } from '@angular/core';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, take, mergeMap, map } from 'rxjs/operators';
import { format, parse, formatDistanceToNow, Locale } from 'date-fns';
import { LocaleService } from '../locale-service/locale.service';
import { Logger, LoggingService } from '../logging/logging.service';
import { DateTimeUtils } from './date-utils';

@Injectable({
    providedIn: 'root'
})
export class DateHandlerService {
    private logger: Logger;
    private currentLocale: Locale;

    constructor(
        loggingService: LoggingService,
        private localeService: LocaleService
    ) {
        this.logger = loggingService.getLogger('date-handler-service');
    }

    initialize(): void {
        this.localeService.localeChanged.subscribe(_ => {
            this.currentLocale = this.detectLocale();
        });
    }

    private detectLocale(): Locale {
        const localeStr = this.localeService.getLocale();
        return DateTimeUtils.detectLocale(localeStr);
    }

    /**
     * Returns a formatted localized date based on the localeService and format provided.
     * Date formatting options can be seen here https://date-fns.org/v3.6.0/docs/format
     *
     * @param date date in Date/string type that needs to be localized and formatted.
     * @param dateFormat format of date as mentioned in above link
     * @returns Localized and formatted date string.
     */
    formatDate(date: Date | string, dateFormat: string): string {
        try {
            if (typeof date === 'string') {
                date = DateTimeUtils.parseISODate(date);
            }
            return format(date, dateFormat, { locale: this.currentLocale });
        } catch (error) {
            this.logger.error('Error formatting date:', error);
        }
        return undefined;
    }

    parseDate(dateStr: string, dateFormat: string): Date {
        return parse(dateStr, dateFormat, new Date(), { locale: this.currentLocale });
    }

    /**
     * Returns whether dateString provided is valid or not
     *
     * @param dateString Date to be checked in string format.
     * @param dateFormat format of provided date as per  https://date-fns.org/v3.6.0/docs/format
     * @returns Boolean dateString is valid or not.
     */
    isValidDateTime(dateString: string, dateFormat: string): boolean {
        const parsedDate = this.parseDate(dateString, dateFormat);
        return DateTimeUtils.isValidDate(parsedDate);
    }

    /**
     * Returns a localized time interval between given date and now based
     * on the localeService and JS toLocaleDateString method
     *
     * @param date Date from which the interval to now is to be calculated
     * @param options includeSeconds, addSuffic and locale.
     * @returns the distance of given date from now as localized string
     */
    fromNow(date: Date | string, options?: {}) {
        if (typeof date === 'string') {
            date = DateTimeUtils.parseISODate(date);
        }
        return formatDistanceToNow(date, { ...options, locale: this.currentLocale });
    }

    /**
     * Returns a localized date based on the localeService and JS toLocaleDateString method
     *
     * @param dateString that needs to be localized
     * @param options that can be passed to toLocaleDateString method
     * @returns the localized string wrapped in an Observable. If input string is null/undefined,
     *          it will return null wrapped in an Observable.
     */
    public getLocalizedDate(dateString: string, options = null): Observable<string> {
        if (!dateString) {
            return of(null);
        }

        return this.localeService.localeChanged.pipe(
            take(1),
            mergeMap(locale => {
                const date = new Date(dateString);
                // Checking for Invalid Date
                if (isNaN(date.getTime())) {
                    return throwError('Invalid Date');
                }
                // locale should have '-' instead of '_'
                const loc = locale.replace('_', '-');
                if (!options) {
                    options = {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    };
                }
                const localizedDateString = `${date.toLocaleDateString(loc, options)}`;
                return of(localizedDateString);
            }),
            catchError(err => {
                this.logger.info('Error while localizing date ', err);
                return throwError(err);
            })
        );
    }

    /**
     * Returns a localized date based on the localeService and JS toLocaleDateString method
     *
     * @param dateString that needs to be localized
     * @param options that can be passed to toLocaleDateString method
     * @returns the localized string wrapped in an Observable. If input string is null/undefined,
     *          it will return null wrapped in an Observable.
     */
    public getLocalizedTime(dateString: string, options = null): Observable<string> {
        if (!dateString) {
            return of(null);
        }

        return this.localeService.localeChanged.pipe(
            take(1),
            mergeMap(locale => {
                const date = new Date(dateString);
                // Checking for Invalid Date
                if (isNaN(date.getTime())) {
                    return throwError('Invalid Date');
                }
                // locale should have '-' instead of '_'
                const loc = locale.replace('_', '-');
                if (!options) {
                    options = {
                        hour: 'numeric',
                        minute: '2-digit'
                    };
                }
                const localizedTimeString = `${date.toLocaleTimeString(loc, options)}`;
                return of(localizedTimeString);
            }),
            catchError(err => {
                this.logger.info('Error while localizing time ', err);
                return throwError(err);
            })
        );
    }

    public getLocalizedDateTime(
        dateString: string,
        optionsDate = null,
        optionsTime = null
    ): Observable<{ [key: string]: string }> {
        return combineLatest([
            this.getLocalizedDate(dateString, optionsDate),
            this.getLocalizedTime(dateString, optionsTime)
        ]).pipe(
            take(1),
            map(([date, time]) => ({
                date,
                time
            }))
        );
    }
    /**
     * Formats a time string to ensure "am" and "pm" (lowercase) are converted to uppercase ("AM", "PM").
     * This function specifically targets the lowercase versions often found on iOS.
     *
     * @param timeString The input time string, potentially containing " am" or " pm".
     * @returns A new string with "am" replaced by "AM" and "pm" replaced by "PM", or the original string if no replacements were needed.
     */
    public formatAMPMToUpperCase(timeString: string): string {
        if (!timeString) {
            return '';
        }

        const updatedString = timeString.replace(/\bam\b/, 'AM').replace(/\bpm\b/, 'PM');

        return updatedString;
    }
}
